/**
 * The payment service's state models, as data and pure functions (ADR 0015).
 *
 *     approved payroll version ─▶ prepare batch ─▶ authorise ─▶ transmit / export ─▶ reconcile
 *
 * FOUR RECORDS, FOUR MACHINES, KEPT APART ON PURPOSE.
 *   - A BATCH is what the owner authorises: an exact, digested set of obligations and destinations.
 *   - An OBLIGATION is what an employee is owed from one source (a payroll result, an off-cycle payment,
 *     a Full & Final settlement). It is paid at most once.
 *   - An ATTEMPT is one try at paying an obligation through one channel. An obligation can have many; at
 *     most one may ever succeed, and a new one may start only after the last one DEFINITIVELY failed.
 *   - A HOLD withholds an employee's unpaid obligations once the owner has put it into effect.
 *
 * WHAT THIS MODULE DECIDES and every implementation must agree with: the states, the legal edges, which
 * obligations a batch may take, when a retry is allowed, and how an obligation's state is projected onto
 * the monotonic `payroll_result_outputs.payment_status` of ADR 0014 without ever moving it backwards.
 *
 * WHAT IT DOES NOT DO: it holds no credentials, talks to no bank, and knows no file format. The adapter seam
 * at the bottom is TYPES ONLY; implementations are server-side.
 *
 * RN-SAFE: no Node built-ins, no I/O, no dates, and money only as decimal STRINGS.
 */

import { PAYMENT_RANK, type PaymentStatus } from './payroll-lifecycle.js';

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

/**
 * How money leaves. A channel is only offered where the configured provider supports it: `upi_payout`
 * in particular needs a provider with a payout API, and is never assumed.
 */
export const PAYMENT_CHANNELS = ['bank_file', 'bank_api', 'upi_payout'] as const;
export type PaymentChannel = (typeof PAYMENT_CHANNELS)[number];

/** Where an obligation comes from. Every obligation traces to exactly one ledger record. */
export const PAYMENT_SOURCE_TYPES = ['payroll_result', 'off_cycle', 'fnf_settlement'] as const;
export type PaymentSourceType = (typeof PAYMENT_SOURCE_TYPES)[number];

/** What a provider connection can do. A capability that is absent is refused, never emulated. */
export interface PaymentProviderCapabilities {
  readonly channels: readonly PaymentChannel[];
  /** It can report a submitted payment's status on request (how an `unknown` outcome is resolved). */
  readonly statusQuery: boolean;
  /** It can cancel a payment that is submitted but not settled. */
  readonly cancellation: boolean;
  /** It pushes signed callbacks. */
  readonly callbacks: boolean;
}

// ---------------------------------------------------------------------------
// The machines
// ---------------------------------------------------------------------------

export interface Transition<S extends string> {
  readonly from: S;
  readonly to: S;
  /** Who or what makes the step, for the reader; the routes enforce it. */
  readonly by: string;
}

function edges<S extends string>(list: readonly Transition<S>[]): readonly Transition<S>[] {
  return list;
}

export function isTransition<S extends string>(
  table: readonly Transition<S>[],
  from: S,
  to: S,
): boolean {
  return table.some((e) => e.from === from && e.to === to);
}

export function nextStates<S extends string>(table: readonly Transition<S>[], from: S): S[] {
  return table.filter((e) => e.from === from).map((e) => e.to);
}

/**
 * A BATCH.
 *
 * `prepared` has a frozen content digest. Authorisation binds to that digest: anything that would change
 * an amount, a destination or a beneficiary moves the batch to `invalidated` (terminal), and the
 * obligations go back to `open` for a new batch. A batch that was EXPORTED for upload cannot also be
 * transmitted through an API: the channel change is `exported → invalidated` with an attestation that
 * the file was not uploaded, followed by a new batch and a new authorisation.
 */
export const PAYMENT_BATCH_STATES = [
  'prepared',
  'pending_authorisation',
  'authorised',
  'exported',
  'transmitting',
  'submitted',
  'completed',
  'rejected',
  'invalidated',
  'cancelled',
] as const;
export type PaymentBatchState = (typeof PAYMENT_BATCH_STATES)[number];

export const PAYMENT_BATCH_TRANSITIONS = edges<PaymentBatchState>([
  { from: 'prepared', to: 'pending_authorisation', by: 'preparer submits it' },
  { from: 'prepared', to: 'cancelled', by: 'preparer' },
  { from: 'prepared', to: 'invalidated', by: 'contents changed' },
  { from: 'pending_authorisation', to: 'authorised', by: 'authoriser, on the exact digest' },
  { from: 'pending_authorisation', to: 'rejected', by: 'authoriser' },
  { from: 'pending_authorisation', to: 'invalidated', by: 'contents changed' },
  { from: 'pending_authorisation', to: 'cancelled', by: 'preparer' },
  { from: 'authorised', to: 'exported', by: 'transmitter generates the bank file' },
  { from: 'authorised', to: 'transmitting', by: 'transmitter sends it to the provider' },
  { from: 'authorised', to: 'invalidated', by: 'contents changed' },
  { from: 'authorised', to: 'cancelled', by: 'preparer, before anything left' },
  {
    from: 'exported',
    to: 'submitted',
    by: 'transmitter records the upload and the bank reference',
  },
  {
    from: 'exported',
    to: 'invalidated',
    by: 'channel change, attesting the file was not uploaded',
  },
  { from: 'transmitting', to: 'submitted', by: 'worker, once every attempt is with the provider' },
  { from: 'submitted', to: 'completed', by: 'every obligation reached an outcome' },
]);

export const TERMINAL_BATCH_STATES: ReadonlySet<PaymentBatchState> = new Set([
  'completed',
  'rejected',
  'invalidated',
  'cancelled',
]);

/** A batch that still claims its obligations: no other batch may take them. */
export function isActiveBatch(state: PaymentBatchState): boolean {
  return !TERMINAL_BATCH_STATES.has(state);
}

/** Before money can have left: the only states whose contents may be invalidated or cancelled. */
export function batchIsBeforeTransmission(state: PaymentBatchState): boolean {
  return state === 'prepared' || state === 'pending_authorisation' || state === 'authorised';
}

/**
 * An OBLIGATION.
 *
 * `paid` is terminal: a successful attempt blocks every further attempt. `failed` means the latest attempt
 * definitively failed and nothing is in flight; it is the only state a retry starts from. A hold takes
 * effect on `open` and `failed` obligations only — never on one in a batch that has been transmitted, and
 * never on `paid` (a hold cannot un-credit money).
 */
export const PAYMENT_OBLIGATION_STATES = [
  'open',
  'held',
  'batched',
  'in_flight',
  'paid',
  'failed',
  'cancelled',
] as const;
export type PaymentObligationState = (typeof PAYMENT_OBLIGATION_STATES)[number];

export const PAYMENT_OBLIGATION_TRANSITIONS = edges<PaymentObligationState>([
  { from: 'open', to: 'batched', by: 'batch preparation' },
  { from: 'open', to: 'held', by: 'a hold takes effect' },
  { from: 'open', to: 'cancelled', by: 'the source was withdrawn before payment' },
  { from: 'held', to: 'open', by: 'the hold is released' },
  { from: 'held', to: 'cancelled', by: 'the source was withdrawn before payment' },
  { from: 'batched', to: 'open', by: 'the batch was cancelled, rejected or invalidated' },
  {
    from: 'batched',
    to: 'held',
    by: 'a hold takes effect before transmission (invalidates the batch)',
  },
  { from: 'batched', to: 'in_flight', by: 'an attempt was queued' },
  { from: 'in_flight', to: 'paid', by: 'an attempt succeeded' },
  { from: 'in_flight', to: 'failed', by: 'the attempt definitively failed' },
  { from: 'failed', to: 'in_flight', by: 'a retry to the same authorised destination' },
  {
    from: 'failed',
    to: 'batched',
    by: 'a retry batch (a changed destination needs a new authorisation)',
  },
  { from: 'failed', to: 'held', by: 'a hold takes effect' },
  { from: 'failed', to: 'cancelled', by: 'the source was withdrawn' },
]);

/**
 * An ATTEMPT.
 *
 * A timeout is `unknown`, never `failed`: the money may have left. An unknown attempt is resolved by a
 * provider status query, an authenticated callback, or a reconciliation with evidence — and until it is,
 * no retry may start. Callbacks may arrive out of order, so an outcome is never overwritten by an earlier
 * stage (`processing` after `succeeded` is ignored, not applied).
 */
export const PAYMENT_ATTEMPT_STATES = [
  'queued',
  'submitted',
  'processing',
  'succeeded',
  'failed',
  'unknown',
  'cancelled',
] as const;
export type PaymentAttemptState = (typeof PAYMENT_ATTEMPT_STATES)[number];

export const PAYMENT_ATTEMPT_TRANSITIONS = edges<PaymentAttemptState>([
  { from: 'queued', to: 'submitted', by: 'the provider accepted it, or the file was uploaded' },
  { from: 'queued', to: 'failed', by: 'refused before submission (nothing left)' },
  { from: 'queued', to: 'cancelled', by: 'the batch was cancelled before transmission' },
  { from: 'queued', to: 'unknown', by: 'the submission timed out' },
  { from: 'submitted', to: 'processing', by: 'provider event' },
  { from: 'submitted', to: 'succeeded', by: 'provider event or reconciliation' },
  { from: 'submitted', to: 'failed', by: 'provider event or reconciliation' },
  { from: 'submitted', to: 'unknown', by: 'no answer within the provider window' },
  { from: 'submitted', to: 'cancelled', by: 'provider-supported cancellation' },
  { from: 'processing', to: 'succeeded', by: 'provider event or reconciliation' },
  { from: 'processing', to: 'failed', by: 'provider event or reconciliation' },
  { from: 'processing', to: 'unknown', by: 'no answer within the provider window' },
  { from: 'processing', to: 'cancelled', by: 'provider-supported cancellation' },
  { from: 'unknown', to: 'processing', by: 'a status query or callback' },
  { from: 'unknown', to: 'succeeded', by: 'a status query, callback or reconciliation' },
  { from: 'unknown', to: 'failed', by: 'a status query, callback or reconciliation' },
]);

/** Outcomes that end an attempt. `unknown` is NOT one of them. */
export const DEFINITIVE_ATTEMPT_STATES: ReadonlySet<PaymentAttemptState> = new Set([
  'succeeded',
  'failed',
  'cancelled',
]);

/**
 * How far along an attempt is, used to apply out-of-order events: an event whose stage is not further
 * than the attempt's current one is recorded as evidence and does not move the attempt.
 */
const ATTEMPT_STAGE: Readonly<Record<PaymentAttemptState, number>> = {
  queued: 0,
  submitted: 1,
  unknown: 1,
  processing: 2,
  succeeded: 3,
  failed: 3,
  cancelled: 3,
};

/**
 * The state an attempt should move to when an event reports `reported`, or `null` to leave it where it is.
 * A definitive outcome is final: a later, contradicting outcome is not applied here and must be raised for
 * reconciliation (`conflict`), because the event history — not the latest message — is the evidence.
 */
export function applyAttemptEvent(
  current: PaymentAttemptState,
  reported: PaymentAttemptState,
): { next: PaymentAttemptState | null; conflict: boolean } {
  if (DEFINITIVE_ATTEMPT_STATES.has(current)) {
    return {
      next: null,
      conflict: reported !== current && DEFINITIVE_ATTEMPT_STATES.has(reported),
    };
  }
  if (reported === current) return { next: null, conflict: false };
  if (!isTransition(PAYMENT_ATTEMPT_TRANSITIONS, current, reported)) {
    return { next: null, conflict: false };
  }
  if (ATTEMPT_STAGE[reported] < ATTEMPT_STAGE[current]) return { next: null, conflict: false };
  return { next: reported, conflict: false };
}

// ---------------------------------------------------------------------------
// Retry
// ---------------------------------------------------------------------------

export interface RetryFacts {
  readonly obligationState: PaymentObligationState;
  readonly attempts: readonly { readonly state: PaymentAttemptState }[];
  /** The destination the batch authorised, and the one the employee has now (their snapshot ids). */
  readonly authorisedDestinationId: string;
  readonly currentDestinationId: string;
  readonly heldNow: boolean;
}

export type RetryVerdict =
  | { readonly kind: 'retry_in_batch' }
  | { readonly kind: 'needs_new_batch'; readonly reason: 'destination_changed' }
  | {
      readonly kind: 'refused';
      readonly reason: 'already_paid' | 'in_flight' | 'unresolved' | 'not_failed' | 'held';
    };

/**
 * Whether an obligation may be retried, and how. Only a DEFINITIVELY failed, unpaid obligation with no
 * attempt in flight or unresolved may be retried. A retry to the destination the batch authorised is a new
 * attempt inside that batch; a changed destination (the employee's bank change was approved) needs a new
 * batch with its own authorisation, because the old authorisation named the old account.
 */
export function retryVerdict(f: RetryFacts): RetryVerdict {
  if (f.obligationState === 'paid' || f.attempts.some((a) => a.state === 'succeeded')) {
    return { kind: 'refused', reason: 'already_paid' };
  }
  if (f.attempts.some((a) => a.state === 'unknown'))
    return { kind: 'refused', reason: 'unresolved' };
  if (f.attempts.some((a) => !DEFINITIVE_ATTEMPT_STATES.has(a.state))) {
    return { kind: 'refused', reason: 'in_flight' };
  }
  if (f.obligationState !== 'failed') return { kind: 'refused', reason: 'not_failed' };
  if (f.heldNow) return { kind: 'refused', reason: 'held' };
  if (f.currentDestinationId !== f.authorisedDestinationId) {
    return { kind: 'needs_new_batch', reason: 'destination_changed' };
  }
  return { kind: 'retry_in_batch' };
}

// ---------------------------------------------------------------------------
// Batch preparation
// ---------------------------------------------------------------------------

export interface CandidateObligation {
  readonly obligationId: string;
  readonly state: PaymentObligationState;
  readonly currency: string;
  /** Decimal STRING. */
  readonly amount: string;
  /** Whether the obligation is already claimed by another batch that is still active. */
  readonly inActiveBatch: boolean;
  readonly heldNow: boolean;
  /** Whether the employee has a usable destination for the channel (validated server-side). */
  readonly destinationReady: boolean;
}

export type ExclusionReason =
  | 'held'
  | 'already_paid'
  | 'in_other_batch'
  | 'in_flight'
  | 'currency_mismatch'
  | 'no_destination'
  | 'zero_amount'
  | 'negative_amount'
  | 'cancelled';

/**
 * Which candidates a batch may take, and why each other one is left out. Held obligations are excluded
 * from the releasable amount; an obligation claimed by another active batch is never taken twice.
 */
export function selectForBatch(
  candidates: readonly CandidateObligation[],
  currency: string,
): { included: string[]; excluded: { obligationId: string; reason: ExclusionReason }[] } {
  const included: string[] = [];
  const excluded: { obligationId: string; reason: ExclusionReason }[] = [];
  for (const c of candidates) {
    const reason = exclusionReason(c, currency);
    if (reason) excluded.push({ obligationId: c.obligationId, reason });
    else included.push(c.obligationId);
  }
  return { included, excluded };
}

function exclusionReason(c: CandidateObligation, currency: string): ExclusionReason | null {
  if (c.state === 'paid') return 'already_paid';
  if (c.state === 'cancelled') return 'cancelled';
  if (c.state === 'in_flight') return 'in_flight';
  if (c.inActiveBatch || c.state === 'batched') return 'in_other_batch';
  if (c.heldNow || c.state === 'held') return 'held';
  if (c.currency !== currency) return 'currency_mismatch';
  if (/^-/.test(c.amount.trim()) && !/^-0*(\.0*)?$/.test(c.amount.trim())) return 'negative_amount';
  if (/^-?0*(\.0*)?$/.test(c.amount.trim())) return 'zero_amount';
  if (!c.destinationReady) return 'no_destination';
  return null;
}

// ---------------------------------------------------------------------------
// Projection onto the ADR 0014 output track
// ---------------------------------------------------------------------------

/**
 * The `payroll_result_outputs.payment_status` an obligation's progress supports, or `null` for "no
 * change". The output track is monotonic and has no edge out of `failed` (ADR 0014), so a failure that
 * can still be retried is NOT written to it: the obligation carries the failure, and the output stays at
 * the last stage money reached. Only an obligation closed without payment writes `failed`.
 *
 * `current` is the output row's status now. An obligation can go BACK to `batched` (a batch invalidated
 * after export, a retry batch after a failure) while the output already records a later stage, so the
 * projection alone would move the track backwards; a status that does not raise `PAYMENT_RANK` above
 * `current` is therefore "no change". A non-null answer can still be more than one edge ahead of
 * `current` (for example `pending` to `submitted`); the writer walks `PAYMENT_TRANSITIONS` to it.
 */
export function outputStatusFor(
  obligation: PaymentObligationState,
  batch: PaymentBatchState | null,
  current: PaymentStatus,
): PaymentStatus | null {
  const target = projectedOutputStatus(obligation, batch);
  if (target === null || PAYMENT_RANK[target] <= PAYMENT_RANK[current]) return null;
  return target;
}

function projectedOutputStatus(
  obligation: PaymentObligationState,
  batch: PaymentBatchState | null,
): PaymentStatus | null {
  if (obligation === 'paid') return 'paid';
  if (obligation === 'cancelled') return 'failed';
  if (obligation === 'in_flight') return batch === 'exported' ? 'file_generated' : 'submitted';
  if (obligation === 'batched') {
    if (batch === 'exported') return 'file_generated';
    if (batch === 'submitted' || batch === 'transmitting') return 'submitted';
    return 'pending';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Summary figures
// ---------------------------------------------------------------------------

export interface ObligationCounts {
  readonly open: number;
  readonly held: number;
  readonly batched: number;
  readonly in_flight: number;
  readonly paid: number;
  readonly failed: number;
  readonly cancelled: number;
}

/**
 * The credited share, stated with its denominator: credited obligations out of RELEASABLE ones — held and
 * cancelled obligations are excluded, because a held salary is withheld on purpose and is not a failure to
 * pay. It is a success proportion, not a service-level measurement, and is labelled as one. `null` when
 * nothing is releasable.
 */
export function creditedShare(
  c: ObligationCounts,
): { numerator: number; denominator: number } | null {
  const denominator = c.open + c.batched + c.in_flight + c.paid + c.failed;
  if (denominator === 0) return null;
  return { numerator: c.paid, denominator };
}

// ---------------------------------------------------------------------------
// Salary holds
// ---------------------------------------------------------------------------

/**
 * A manager REQUESTS; the payroll desk PREPARES it for approval (or raises it directly); the owner puts it
 * into effect. Only an `active` hold withholds anything. Release is its own authorised act and returns the
 * employee's unpaid, held obligations to `open`.
 */
export const SALARY_HOLD_STATES = [
  'requested',
  'pending_approval',
  'active',
  'released',
  'rejected',
  'withdrawn',
] as const;
export type SalaryHoldState = (typeof SALARY_HOLD_STATES)[number];

export const SALARY_HOLD_TRANSITIONS = edges<SalaryHoldState>([
  { from: 'requested', to: 'pending_approval', by: 'payroll desk prepares it' },
  { from: 'requested', to: 'rejected', by: 'payroll desk declines it' },
  { from: 'requested', to: 'withdrawn', by: 'requester' },
  { from: 'pending_approval', to: 'active', by: 'owner approves' },
  { from: 'pending_approval', to: 'rejected', by: 'owner rejects' },
  { from: 'pending_approval', to: 'withdrawn', by: 'payroll desk or requester' },
  { from: 'active', to: 'released', by: 'owner releases' },
]);

export const SALARY_HOLD_REASONS = [
  'asset_handover',
  'absconding',
  'investigation',
  'bank_details_unverified',
  'legal_order',
  'other',
] as const;
export type SalaryHoldReason = (typeof SALARY_HOLD_REASONS)[number];

// ---------------------------------------------------------------------------
// Off-cycle payments
// ---------------------------------------------------------------------------

/**
 * An off-cycle payment is a CALCULATION inside the payroll ledger — it is taxed, it enters year-to-date
 * totals and later runs' baselines — and only its approved result becomes an obligation. It is never an
 * arbitrary transfer.
 */
export const OFF_CYCLE_STATES = [
  'draft',
  'calculating',
  'calculated',
  'pending_approval',
  'approved',
  'paid',
  'rejected',
  'cancelled',
] as const;
export type OffCycleState = (typeof OFF_CYCLE_STATES)[number];

export const OFF_CYCLE_TRANSITIONS = edges<OffCycleState>([
  { from: 'draft', to: 'calculating', by: 'payroll desk' },
  { from: 'draft', to: 'cancelled', by: 'payroll desk' },
  { from: 'calculating', to: 'calculated', by: 'engine' },
  { from: 'calculating', to: 'draft', by: 'engine failure (the draft stays editable)' },
  { from: 'calculated', to: 'calculating', by: 'recalculation after an edit' },
  { from: 'calculated', to: 'pending_approval', by: 'payroll desk submits' },
  { from: 'calculated', to: 'cancelled', by: 'payroll desk' },
  { from: 'pending_approval', to: 'approved', by: 'owner approves the exact calculation' },
  { from: 'pending_approval', to: 'rejected', by: 'owner rejects' },
  { from: 'rejected', to: 'draft', by: 'payroll desk revises it' },
  { from: 'approved', to: 'paid', by: 'its obligation was paid' },
]);

export const OFF_CYCLE_CATEGORIES = [
  'arrears',
  'bonus',
  'incentive',
  'reimbursement',
  'correction',
  'advance',
  'other',
] as const;
export type OffCycleCategory = (typeof OFF_CYCLE_CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Payslip publication policy
// ---------------------------------------------------------------------------

/**
 * When a payslip is published relative to payment. Explicit, per legal entity: a failed transfer does not
 * by itself withhold a payslip, because the payslip records what was EARNED, not what was credited.
 */
export const PAYSLIP_PUBLICATION_POLICIES = [
  'on_release',
  'after_payment_confirmed',
  'manual',
] as const;
export type PayslipPublicationPolicy = (typeof PAYSLIP_PUBLICATION_POLICIES)[number];

// ---------------------------------------------------------------------------
// The adapter seam. TYPES ONLY — implemented server-side, never in this package.
// ---------------------------------------------------------------------------

/** One line a provider is asked to pay. The destination is opened server-side, never sent to a client. */
export interface PaymentInstruction {
  readonly attemptId: string;
  /** The provider-facing idempotency key: stable across retries of the SAME attempt, new per attempt. */
  readonly idempotencyKey: string;
  readonly amount: string;
  readonly currency: string;
  readonly destinationSnapshotId: string;
}

export type ProviderSubmission =
  | { readonly kind: 'accepted'; readonly providerReference: string }
  | { readonly kind: 'rejected'; readonly code: string; readonly message: string }
  | { readonly kind: 'unknown'; readonly detail: string };

export interface ProviderStatus {
  readonly state: PaymentAttemptState;
  readonly providerReference: string | null;
  /** The bank's own reference (UTR, trace number) once known. */
  readonly bankReference: string | null;
  readonly failureCode: string | null;
}

/**
 * What a bank or provider integration implements. Bank-file adapters produce a file and do not submit;
 * API adapters submit. Neither is assumed: the capabilities say which.
 */
export interface PaymentAdapter {
  readonly id: string;
  readonly capabilities: PaymentProviderCapabilities;
  generateFile?(batch: {
    readonly batchId: string;
    readonly instructions: readonly PaymentInstruction[];
  }): Promise<{ readonly content: Uint8Array; readonly fileName: string; readonly format: string }>;
  submit?(instruction: PaymentInstruction): Promise<ProviderSubmission>;
  queryStatus?(providerReference: string): Promise<ProviderStatus>;
  cancel?(providerReference: string): Promise<{ readonly cancelled: boolean }>;
}
